import { useEffect, useState } from "react";
import type { MyAffiliateLinkView } from "@keydials/api-client";
import { api, getAccessToken } from "../auth/AuthContext";
import { referralLinkUrl } from "../referralCode";
import { SettingsCard } from "./SettingsCard";

function pounds(pence: number): string {
  return (pence / 100).toLocaleString("en-GB", { style: "currency", currency: "GBP" });
}

/**
 * "Your referral links" (KEY-514): an affiliate's own code and link, ready to copy. Renders nothing for
 * anybody who is not an affiliate — nearly everybody — and nothing where the affiliate module is off.
 *
 * The link opens the website (their partner's page where it is published) with `?ref=` on it; the site
 * remembers it for 30 days and carries it to checkout, where a new customer's purchase is credited to them.
 */
export function ReferralLinksCard() {
  const [links, setLinks] = useState<MyAffiliateLinkView[]>([]);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    let cancelled = false;
    api
      .getMyAffiliateLinks(token)
      .then((items) => !cancelled && setLinks(items))
      // A nicety: if it can't be read, the card simply isn't shown.
      .catch(() => !cancelled && setLinks([]));
    return () => {
      cancelled = true;
    };
  }, []);

  if (links.length === 0) return null;

  const copy = async (url: string, profileId: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(profileId);
    } catch {
      window.prompt("Copy your link", url);
    }
  };

  return (
    <SettingsCard
      title="Your referral links"
      hint="Share your link. Anyone who signs up through it within 30 days is credited to you."
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {links.map((link) => {
          const url = referralLinkUrl(link.referralCode, link.fundingPartnerPageSlug);
          return (
            <div key={link.profileId} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                {link.organisationName}
                {!link.isActive && <strong style={{ color: "var(--st-amber, #b45309)" }}> · paused</strong>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <code style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5 }}>{link.referralCode}</code>
                <button
                  type="button"
                  className="kd-btn kd-btn-sm"
                  onClick={() => void copy(url, link.profileId)}
                  disabled={!link.isActive}
                >
                  {copied === link.profileId ? "Copied" : "Copy link"}
                </button>
              </div>
              <div style={{ fontSize: 12.5, color: "var(--text-faint)", wordBreak: "break-all" }}>{url}</div>
              <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                {link.referralCount === 1 ? "1 sign-up" : `${link.referralCount} sign-ups`} so far
                {link.commissionPence > 0 && <> · {pounds(link.commissionPence)} commission earned</>}
              </div>
            </div>
          );
        })}
      </div>
    </SettingsCard>
  );
}
